import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

export enum EllipsisType {
  begin = "begin",
  middle = "middle",
  end = "end",
}

export function useFilters() {
  const date = (value: string | null, showTime = false, timezone: string | null = null): string => {
    if (!value) return "—";
    let format = "MM/DD/YYYY";
    let date = dayjs(value);
    if (showTime) format += " HH:mm";

    if (timezone) {
      date = date.tz(timezone);
    }

    return date.format(format);
  };

  const format = (value: string, fractionDigits = 2): string => {
    const num = parseFloat(value);
    return num !== Math.floor(num) ? num.toFixed(fractionDigits) : num.toString();
  };

  const price = (value: string): string => {
    const num = parseFloat(value);
    return "$" + num.toFixed(2);
  };

  const initials = (name: string | null): string => {
    if (!name) return "";
    const names = name.split(" ");
    return names.length > 1 ? names[0][0].toUpperCase() + names[1][0].toUpperCase() : name.slice(0, 2).toUpperCase();
  };

  const miles = (
    value: number | null | undefined,
    decimals = 2,
    rounding: "none" | "floor" | "ceil" | "round" = "none",
  ): string => {
    if (value === null || value === undefined) {
      return "";
    }

    let result = value / 1609.344;

    if (rounding !== "none") {
      const factor = Math.pow(10, decimals);
      result = Math[rounding](result * factor) / factor;
    }

    return result.toFixed(decimals);
  };

  const hours = (value: string | null): string => {
    if (!value) return "";
    return (parseFloat(value) / 60 / 60).toFixed(2);
  };

  const mdash = (value: string | null): string => {
    return value || "-";
  };

  const round = (value: string | number, count = 2): number => {
    const num = typeof value === "number" ? value : parseFloat(value);

    if (isNaN(num)) {
      return 0;
    }

    return parseFloat(Math.round(num).toFixed(count));
  };

  const upper = (s: string): string => {
    return s ? s.toUpperCase() : "";
  };

  const ellipsis = (str: string, length = 40, type: EllipsisType = EllipsisType.end, dots = "..."): string => {
    if (str.length <= length) return str;
    const cropLength = length - dots.length;
    switch (type) {
      case EllipsisType.begin:
        return dots + str.slice(-cropLength);
      case EllipsisType.middle:
        return str.slice(0, Math.floor(cropLength / 2)) + dots + str.slice(-Math.floor(cropLength / 2));
      case EllipsisType.end:
        return str.slice(0, cropLength) + dots;
    }
  };

  const bytes = (bytes: number, decimals = 2): string => {
    if (!+bytes) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  };

  const booleanValue = (value: boolean, trueValue = "true", falseValue = "false"): string => {
    return value ? trueValue : falseValue;
  };

  return {
    date,
    format,
    price,
    initials,
    miles,
    hours,
    mdash,
    round,
    upper,
    ellipsis,
    bytes,
    booleanValue,
  };
}
